// GOING LIVE — readiness read from evidence, plus the two things that
// previously required shell access: a connection test a partner can run
// themselves, and the raw exchanges with the provider.
//
// WHAT THE CONNECTION TEST DOES
//
// Creates a real payment job against the test credentials, reads the
// response, throws it away. Nobody is sent to it and nobody pays it; an
// unpaid job simply expires at the provider.
//
// It is the throwaway probe script that was needed on 25 Aug to work out
// the real response shape — except it is a button. One press proves the
// credentials decrypt, the endpoint is right, the authentication hash is
// correct, and the reply carries a usable hosted-page URL. Every one of
// those cost shell access to establish, and none of them need a
// competition, a demo route or a simulation.
//
// WHY THE HOSTED-PAGE URL IS NOT SHOWN
//
// The backend deliberately does not return it. Nothing should be paid
// through a connection test, and handing back a live payment link
// invites exactly that.

import { useEffect, useState } from "react";
import { siteTime } from "./siteTime.js";

const CHECK_INTERVAL_MS = 15_000;

function ts(ms) {
  try {
    return siteTime(ms);
  } catch {
    return "";
  }
}

export default function GoingLivePanel({
  token,
  wsRequest,
  canEdit = false,
  // WHICH CREDENTIAL SET this panel is reporting on. This instance has
  // two — its own and the partner's — and they go live independently.
  // Defaults to the site's so the existing Payment Providers tab is
  // unchanged by this becoming configurable.
  statusAction = "admin.paymentProvider.goingLive",
  testAction = "admin.paymentProvider.testConnection",
  heading = "Going live",
}) {
  const [status, setStatus] = useState(null);
  const [exchanges, setExchanges] = useState([]);
  const [showRaw, setShowRaw] = useState(false);
  // { i, side } — which exchange is open and which half of it.
  const [expanded, setExpanded] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadAll = () => {
    wsRequest(statusAction, {}, token)
      .then(setStatus)
      .catch(() => {});
    wsRequest("admin.paymentProvider.exchanges", {}, token)
      .then((d) => setExchanges(d?.exchanges || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, CHECK_INTERVAL_MS);
    // RE-CHECK WHEN THIS TAB COMES BACK INTO VIEW. The actual workflow
    // is: switch to the demo site, buy something, switch back here — so
    // regaining focus is the moment the numbers are stale, and waiting
    // out a timer after returning makes the panel look broken even
    // though it would have caught up on its own.
    //
    // visibilitychange rather than window focus: it fires for tab
    // switches, which is the case here, and not for merely clicking
    // back into an already-visible window.
    const onVisible = () => {
      if (document.visibilityState === "visible") loadAll();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const runTest = () => {
    setTesting(true);
    setTestResult(null);
    wsRequest(testAction, {}, token)
      .then((r) => setTestResult(r))
      // A refusal (no provider selected, no site URL set) arrives as a
      // thrown error rather than a result. Shown the same way: to whoever
      // pressed the button it is the same thing — it did not work, and
      // here is why.
      .catch((e) => setTestResult({ ok: false, error: e?.message || String(e) }))
      .finally(() => {
        setTesting(false);
        loadAll();
      });
  };

  const isOpen = (i, side) => expanded?.i === i && expanded.side === side;
  const toggle = (i, side) =>
    setExpanded((cur) => (cur?.i === i && cur.side === side ? null : { i, side }));

  // Styled as a link rather than a button: these reveal text, they do
  // not do anything, and a row of buttons reads as a row of actions.
  const linkish = {
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    fontSize: 11.5,
    opacity: 0.7,
    textDecoration: "underline",
    cursor: "pointer",
    flexShrink: 0,
  };

  const box = {
    border: "1px solid var(--border-color)",
    borderRadius: 6,
    padding: "10px 12px",
    marginBottom: 14,
    fontSize: 13,
  };

  return (
    <div style={{ marginTop: 16, maxWidth: 760 }}>
      <h3>{heading}</h3>

      <div style={box}>
        <div style={{ marginBottom: 6 }}>
          <strong>Test the connection</strong>
        </div>
        <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 8 }}>
          Creates a payment job with the ACTIVE provider's test credentials and reads
          the reply, then discards it. Nothing is charged and nobody is sent to it.
          <br />
          <br />
          To test a specific provider — or LIVE credentials — use the Test
          configuration button inside that provider's own box under Providers. This
          one only ever checks whichever provider is currently active, in test mode.
        </div>
        <button onClick={runTest} disabled={testing || !canEdit}>
          {testing ? "Testing\u2026" : "Test connection"}
        </button>
        {!canEdit && (
          <span style={{ fontSize: 12, opacity: 0.65, marginLeft: 8 }}>
            Your account has view-only access here.
          </span>
        )}
        {testResult && (
          <div style={{ marginTop: 8, fontSize: 12.5, padding: "6px 8px", borderRadius: 4, border: "1px solid var(--border-color)" }}>
            {testResult.ok ? (
              <>
                <strong>Worked.</strong> {testResult.provider} answered in {testResult.ms}ms
                {testResult.providerReference ? ` — reference ${testResult.providerReference}.` : "."}
              </>
            ) : (
              <>
                <strong>Did not work.</strong> {testResult.error}
                {exchanges.length > 0 && " Their exact reply is in the exchanges below."}
              </>
            )}
          </div>
        )}
      </div>

      {status && (
        <div style={box}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <strong>Readiness</strong>
            <span style={{ opacity: 0.75 }}>
              {status.ready ? "Ready \u2014 every check below passed." : "Not ready yet."}
            </span>
            {/* For when you want it now rather than in a few seconds.
                The panel re-checks on a timer and whenever this tab is
                brought back into view, so this is a convenience, not
                the only way the numbers move. */}
            <button onClick={loadAll} style={{ ...linkish, marginLeft: "auto" }}>
              refresh
            </button>
          </div>
          <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 10 }}>
            These are not tickboxes. Each one reports what actually happened on
            this server, using the credentials configured right now — change the
            credentials and the evidence resets, because it would no longer
            describe the account you are about to take money with.
          </div>
          {(status.checks || []).map((c) => (
            <div key={c.id} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
              <span aria-hidden="true" style={{ width: 14, flexShrink: 0, opacity: c.passed ? 1 : 0.4 }}>
                {c.passed ? "\u2713" : "\u00b7"}
              </span>
              <div>
                <div style={{ opacity: c.passed ? 1 : 0.85 }}>{c.label}</div>
                {c.detail && <div style={{ fontSize: 12, opacity: 0.65, marginTop: 1 }}>{c.detail}</div>}
              </div>
            </div>
          ))}
          {status.stillPending?.length > 0 && (
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
              {status.stillPending.length} checkout
              {status.stillPending.length === 1 ? "" : "s"} still pending, oldest{" "}
              {status.stillPending[0].ageDays} day
              {status.stillPending[0].ageDays === 1 ? "" : "s"} old. Usually
              someone who closed the payment page — but a payment whose
              confirmation never arrived would look the same.
            </div>
          )}
        </div>
      )}

      <div style={box}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <strong>What the provider actually said</strong>
          <button onClick={() => setShowRaw((v) => !v)} style={{ marginLeft: "auto" }}>
            {showRaw ? "Hide" : `Show (${exchanges.length})`}
          </button>
        </div>
        {showRaw && (
          <>
            <div style={{ fontSize: 12.5, opacity: 0.75, margin: "8px 0" }}>
              The last {exchanges.length} messages between this server and the
              provider, newest first. <strong>Outgoing</strong> is us calling
              them; <strong>incoming</strong> is their webhook calling us — the
              direction you cannot otherwise observe at all. No keys or card
              details are recorded here.
            </div>
            {exchanges.length === 0 && (
              <div style={{ fontSize: 12.5, opacity: 0.65 }}>
                Nothing yet. Run the connection test above.
              </div>
            )}
            {exchanges.map((e, i) => (
              <div key={i} style={{ borderTop: "1px solid var(--border-color)", padding: "6px 0" }}>
                <div style={{ display: "flex", gap: 10, fontSize: 12.5, alignItems: "center" }}>
                  <span style={{ opacity: 0.6, width: 62, flexShrink: 0 }}>{ts(e.at)}</span>
                  <span style={{ width: 66, flexShrink: 0 }}>{e.direction}</span>
                  <span style={{ width: 34, flexShrink: 0, opacity: 0.8 }}>{e.status ?? "\u2014"}</span>
                  <span style={{ opacity: 0.75, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.method} {e.path}
                  </span>
                  {/* "Request" is what the CALLER sent either way: our JSON
                      going out, their payload coming in. Older entries have
                      no request recorded, so the link is simply not offered
                      rather than opening an empty box. */}
                  {e.requestBody != null && (
                    <button onClick={() => toggle(i, "request")} style={linkish}>
                      {isOpen(i, "request") ? "hide request" : "request"}
                    </button>
                  )}
                  <button onClick={() => toggle(i, "response")} style={linkish}>
                    {isOpen(i, "response") ? "hide response" : "response"}
                  </button>
                </div>
                {expanded?.i === i && (
                  <pre style={{ fontSize: 11.5, whiteSpace: "pre-wrap", wordBreak: "break-all", opacity: 0.85, margin: "6px 0 0", maxHeight: 320, overflow: "auto" }}>
                    {expanded.side === "request" ? e.requestBody : e.body}
                  </pre>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
